<template>
  <div class="daf">

    <!-- ── Sticky toolbar ── -->
    <div class="daf__toolbar">

      <!-- Intensity slider -->
      <div class="daf__toolbar-intensity">
        <ThemeIcon icon="lightning" type="icons" classes="daf__toolbar-icon" />
        <input
          type="range"
          min="10"
          max="100"
          step="5"
          :value="agsOpacityValue"
          @input="onOpacityChange($event.target.value)"
          class="daf__slider"
          :title="`Minimum Intensity: ${opacityPercent}%`"
          aria-label="Minimum intensity"
        />
        <span class="daf__toolbar-pct">{{ opacityPercent }}%</span>
      </div>

      <!-- Comparison toggle -->
      <label
        class="daf__toolbar-btn"
        :class="{ 'daf__toolbar-btn--active': comparisonEnabled }"
        :title="comparisonEnabled ? 'Comparison: On' : 'Comparison: Off'"
      >
        <input
          type="checkbox"
          :checked="comparisonEnabled"
          @change="onComparisonToggle($event.target.checked)"
          class="daf__hidden-input"
        />
        <!-- Scales / compare icon -->
        <svg viewBox="0 0 20 20" fill="currentColor" class="daf__toolbar-svg" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 14a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 5.677V19a1 1 0 11-2 0V5.677L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" clip-rule="evenodd"/>
        </svg>
      </label>

      <!-- Comparison scope (shown inline when enabled) -->
      <transition name="daf-fade">
        <div v-if="comparisonEnabled" class="daf__toolbar-scope">
          <label class="daf__scope-opt" :class="{ 'daf__scope-opt--active': comparisonScope === 'state' }">
            <input type="radio" value="state" :checked="comparisonScope === 'state'" name="daf-scope" @change="onScopeChange('state')" class="daf__hidden-input" />
            State
          </label>
          <label class="daf__scope-opt" :class="{ 'daf__scope-opt--active': comparisonScope === 'county' }">
            <input type="radio" value="county" :checked="comparisonScope === 'county'" name="daf-scope" @change="onScopeChange('county')" class="daf__hidden-input" />
            County
          </label>
        </div>
      </transition>

      <!-- Clear all -->
      <button
        class="daf__toolbar-btn daf__toolbar-btn--danger"
        @click="$emit('clear-all')"
        title="Clear all filters"
        aria-label="Clear all filters"
      >
        <!-- Trash icon -->
        <svg viewBox="0 0 20 20" fill="currentColor" class="daf__toolbar-svg" aria-hidden="true">
          <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- ── Scrollable variable list ── -->
    <div class="daf__list">
      <!-- Category label -->
      <div v-if="datasetCategoryLabel" class="daf__category-label">
        <ThemeIcon icon="tag" type="icons" classes="daf__category-icon" />
        <span>{{ datasetCategoryLabel }}</span>
      </div>

      <div
        v-for="variable in datasetVariables"
        :key="variable.path + '_' + variable.category_id"
        class="daf__item"
      >
        <!-- Variable name -->
        <span class="daf__item-name" :title="variable.label">{{ variable.label }}</span>

        <!-- Scale direction -->
        <button
          class="daf__scale-btn"
          :class="getScaleDirection(variable.path) ? 'daf__scale-btn--pos' : 'daf__scale-btn--neg'"
          :title="getScaleDirection(variable.path) ? 'Higher is Better' : 'Higher is Worse'"
          @click="toggleScaleDirection(variable.path)"
          :aria-label="getScaleDirection(variable.path) ? 'Higher is Better' : 'Higher is Worse'"
        >
          <ThemeIcon icon="arrows-vertical" type="icons" classes="daf__item-icon" />
        </button>

        <!-- Weight (multi only) -->
        <div v-if="isMultiVariable" class="daf__weight">
          <span class="daf__weight-pct">%</span>
          <input
            type="number"
            :value="getVariableWeight(variable.path)"
            @input="onWeightChange(variable.path, $event.target.value)"
            min="0" max="100" step="1"
            class="daf__weight-input"
            :aria-label="`Weight for ${variable.label}`"
          />
        </div>

        <!-- Remove -->
        <button
          class="daf__remove-btn"
          @click="removeVariable(variable)"
          :aria-label="`Remove ${variable.label}`"
        >
          <ThemeIcon icon="x" type="icons" classes="daf__item-icon" />
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'DatasetActiveFilters',
  components: { ThemeIcon },

  props: {
    datasetVariables:     { type: Array,   default: () => [] },
    datasetCategoryLabel: { type: String,  default: '' },
    comparisonEnabled:    { type: Boolean, default: false },
    comparisonScope:      { type: String,  default: 'state' },
    agsOpacityValue:      { type: Number,  default: 70 }
  },

  data() {
    return {
      scaleDirections: {},
      variableWeights: {}
    };
  },

  computed: {
    isMultiVariable() { return this.datasetVariables.length >= 2; },
    opacityPercent()  { return Math.round(this.agsOpacityValue); }
  },

  watch: {
    datasetVariables(n, o) {
      if (n.length !== o.length) this.initializeWeights();
    }
  },

  methods: {
    removeVariable(v) { this.$emit('remove-dataset-variable', v); },

    getScaleDirection(path) { return this.scaleDirections[path] ?? false; },

    toggleScaleDirection(path) {
      this.$set(this.scaleDirections, path, !this.getScaleDirection(path));
      this.$emit('scale-direction-changed', {
        variablePath: path,
        higherIsBetter: this.scaleDirections[path],
        isSingleVariable: !this.isMultiVariable
      });
    },

    getVariableWeight(path) {
      if (!this.isMultiVariable) return 100;
      if (!Object.keys(this.variableWeights).length) this.initializeWeights();
      return Math.round((this.variableWeights[path] || 0) * 100);
    },

    initializeWeights() {
      const n = this.datasetVariables.length;
      if (!n) return;
      const w = {};
      this.datasetVariables.forEach(v => { w[v.path] = 1 / n; });
      this.variableWeights = w;
      this.$emit('weights-changed', w);
    },

    onWeightChange(path, value) {
      const pct = parseFloat(value);
      if (isNaN(pct) || pct < 0) return;
      const nw = Math.min(1, Math.max(0, pct / 100));
      const others = this.datasetVariables.filter(v => v.path !== path);
      this.$set(this.variableWeights, path, nw);
      if (others.length) {
        const rem = 1 - nw;
        const tot = others.reduce((s, v) => s + (this.variableWeights[v.path] || 0), 0);
        others.forEach(v => {
          this.$set(this.variableWeights, v.path,
            tot > 0 ? rem * ((this.variableWeights[v.path] || 0) / tot) : rem / others.length);
        });
      }
      this.$forceUpdate();
      this.$emit('weights-changed', { ...this.variableWeights });
    },

    onComparisonToggle(enabled) {
      this.$emit('comparison-toggled', { enabled, scope: this.comparisonScope });
    },
    onScopeChange(value) {
      this.$emit('comparison-toggled', { enabled: this.comparisonEnabled, scope: value });
    },
    onOpacityChange(value) {
      this.$emit('opacity-changed', { type: 'ags', value: parseFloat(value) / 100 });
    }
  }
};
</script>

<style lang="less">
@import '../../../../../less/theme/v1/components/cresiteiq2/dataset-active-filters.less';
</style>
